/**
 * Choosing the MISSION DEBRIEFING screen's paragraph out of `REVIEW.DAT`.
 *
 * The original does not compose that sentence: it picks one of 52 pre-written
 * messages (`debrief-review-text.ts`) and draws its text.
 * This module is the picker — a section per mission family, then a message
 * within it.
 *
 * **The section mapping is measured; the message rules are inferred.** The
 * mapping is measured in the strong sense that `REVIEW.DAT`'s eleven sections
 * are, in file order, exactly `MISSION_TYPES`' ten families followed by the
 * famous-ace duel: section 2 is the only one that talks about reconnaissance
 * aircraft, section 8 the only one about flaming *enemy* balloons against
 * section 7's *ours*, and so on down the list with no leftovers and no gaps.
 * `MISSION_TYPES`' order came from `RBMB.EXE`'s Mission Builder labels
 * (`mission-types.ts`), years of provenance away from this file, so the two
 * orders agreeing is independent evidence for both. `SECTION_FOR` below is
 * asserted against that list in `debrief-review.test.ts`.
 *
 * The rule that picks a *message* is a different kind of claim, and is marked
 * as such: `PS.EXE`'s selection code has not been disassembled. What each rule
 * below rests on is the message's own wording, which states its condition
 * plainly — *"without losing a single airplane"*, *"Some of our reconnaissance
 * aircraft"*, *"all of our bombers were shot down"*. Where a section grades
 * outcomes the wording orders them completely, so the rules are readings of the
 * text rather than guesses at code. Anything the text leaves genuinely
 * ambiguous is called out at the rule.
 */

import type { MissionType } from "./mission-types.ts";
import { REVIEW_NARRATIVES, type ReviewNarrative } from "./debrief-review-text.ts";

/** The famous-ace duel is not a `MissionType`; it is `REVIEW.DAT`'s eleventh section. */
export const ACE_DUEL_SECTION = 10;

/**
 * `REVIEW.DAT` section per mission family. This is the file's own order — see
 * the module note for why that is a measurement and not an assumption.
 */
export const SECTION_FOR: Readonly<Record<MissionType, number>> = {
  dogfight: 0,
  patrol: 1,
  escortRecon: 2,
  escortBombers: 3,
  stopBombers: 4,
  destroyZeppelin: 5,
  interceptZeppelin: 6,
  balloonDefense: 7,
  balloonBusting: 8,
  scramble: 9,
};

/**
 * Everything the 52 messages between them assert about a sortie, in the terms
 * they assert it in. Assembled by `debrief.ts` from a `MissionRun`; kept as a
 * plain record so the rules below are readable side by side with the prose they
 * implement, and so a test can drive every message without building a mission.
 */
export interface ReviewFacts {
  /**
   * Whether the flight met the enemy at all. Only section 0 and the ace duel
   * distinguish this from "met them and nobody scored" — *"Your flight did not
   * locate the specified enemy fighters"* against *"No aircraft from either
   * side were shot down **in the dogfight**"*.
   */
  readonly madeContact: boolean;
  /** Enemy aircraft (or, for BALLOON BUSTING, enemy balloons) the mission set against the flight. */
  readonly enemyTotal: number;
  readonly enemyDestroyed: number;
  /** Aircraft of the player's own flight that did not come back. */
  readonly friendlyLosses: number;
  /**
   * The charges: the reconnaissance machines, the bombers, our own balloons —
   * whatever the mission's `protect` objective was counting.
   */
  readonly escortTotal: number;
  readonly escortLost: number;
  /** The enemy ground target a bombing raid was sent against. */
  readonly targetsDestroyed: number;
  /** Ours, lost — a bombed aerodrome, a burnt balloon site. */
  readonly friendlyTargetsLost: number;
  /** Whether the Zeppelin went down; only sections 5 and 6 read it. */
  readonly zeppelinDown: boolean;
  readonly playerLost: boolean;
  /** Fills `%t`: the target's plain English noun, e.g. `"supply dump"`. */
  readonly targetNoun: string;
  /** Fills `%n`: the famous ace's name. */
  readonly aceName: string;
}

/** Every count zero, nothing met — the base a caller overrides field by field. */
export const NO_REVIEW_FACTS: ReviewFacts = {
  madeContact: false,
  enemyTotal: 0,
  enemyDestroyed: 0,
  friendlyLosses: 0,
  escortTotal: 0,
  escortLost: 0,
  targetsDestroyed: 0,
  friendlyTargetsLost: 0,
  zeppelinDown: false,
  playerLost: false,
  targetNoun: "target",
  aceName: "the enemy ace",
};

/** True when the flight accounted for every enemy it was sent against. */
function clearedTheSky(facts: ReviewFacts): boolean {
  return facts.enemyTotal > 0 && facts.enemyDestroyed >= facts.enemyTotal;
}

/** True when every machine the flight was covering was lost. */
function escortWipedOut(facts: ReviewFacts): boolean {
  return facts.escortTotal > 0 && facts.escortLost >= facts.escortTotal;
}

/**
 * DOGFIGHT — six messages. The only section with a "did not locate" case, and
 * the only one whose no-score message says *"in the dogfight"*, which is what
 * separates message 1 from message 0.
 */
function dogfightMessage(facts: ReviewFacts): number {
  if (!facts.madeContact || facts.enemyTotal === 0) return 0;
  if (facts.enemyDestroyed === 0) return facts.friendlyLosses === 0 ? 1 : 3;
  if (clearedTheSky(facts)) return facts.friendlyLosses === 0 ? 2 : 5;
  return 4;
}

/**
 * PATROL THE FRONT — five messages, and the only section that singles out a
 * score of exactly one (*"Your flight shot down one enemy aircraft."*, the one
 * message in the file with no verdict sentence). Message 2's *"shot down enemy
 * aircraft"* is plural, so it starts at two.
 */
function patrolMessage(facts: ReviewFacts): number {
  if (facts.enemyDestroyed === 0) return facts.friendlyLosses > 0 ? 4 : 0;
  if (facts.enemyDestroyed === 1) return 1;
  return facts.friendlyLosses === 0 ? 2 : 3;
}

/**
 * ESCORT RECONNAISSANCE — four messages, graded purely on how many of the
 * charges came home; message 0 adds *"and fighters"*, so it also wants a clean
 * escort.
 */
function escortReconMessage(facts: ReviewFacts): number {
  if (facts.escortLost === 0) return facts.friendlyLosses === 0 ? 0 : 1;
  if (escortWipedOut(facts)) return 3;
  return 2;
}

/**
 * ESCORT A BOMBING RAID — seven messages, the largest section, and the only one
 * that reads as a full two-axis matrix: was the target destroyed, and what did
 * the bombers pay. Both "all bombers lost" rows exist separately from "some
 * lost", which is why `escortWipedOut` is tested before the partial case.
 */
function escortBombersMessage(facts: ReviewFacts): number {
  if (facts.targetsDestroyed > 0) {
    if (escortWipedOut(facts)) return 3;
    if (facts.escortLost > 0) return 2;
    return facts.friendlyLosses === 0 ? 0 : 1;
  }
  if (escortWipedOut(facts)) return 6;
  if (facts.escortLost > 0) return 5;
  return 4;
}

/**
 * STOP A BOMBING RAID — five messages. Here the bombers are the *enemy*, and
 * `%t` is ours. Message 2 (*"Our %t survived the bombing raid."*) is the plain
 * case that neither wiped out the raid nor lost the target.
 */
function stopBombersMessage(facts: ReviewFacts): number {
  const lostTheTarget = facts.friendlyTargetsLost > 0;
  const wipedOutTheRaid = clearedTheSky(facts);
  if (!lostTheTarget) {
    if (!wipedOutTheRaid) return 2;
    return facts.friendlyLosses === 0 ? 0 : 1;
  }
  return wipedOutTheRaid ? 3 : 4;
}

/**
 * HUNT A ZEPPELIN — three messages. Messages 0 and 1 are the same sentence with
 * different verdicts, so something must separate them; a clean sweep is the
 * reading every other section in the file uses for its "outstanding" row.
 * **This is the one genuinely ambiguous split in the file** — the text does not
 * say what makes it outstanding, and the rule is by analogy.
 */
function destroyZeppelinMessage(facts: ReviewFacts): number {
  if (!facts.zeppelinDown) return 2;
  return facts.friendlyLosses === 0 ? 0 : 1;
}

/** HUNT A ZEPPELIN, defending — four messages, graded on the Zeppelin and on our `%t`. */
function interceptZeppelinMessage(facts: ReviewFacts): number {
  if (!facts.zeppelinDown) return 3;
  if (facts.friendlyTargetsLost > 0) return 2;
  return facts.friendlyLosses === 0 ? 0 : 1;
}

/** BALLOON DEFENSE — four messages, on how many of *our* balloons survived. */
function balloonDefenseMessage(facts: ReviewFacts): number {
  if (facts.escortLost === 0) return facts.friendlyLosses === 0 ? 0 : 1;
  if (escortWipedOut(facts)) return 3;
  return 2;
}

/** BALLOON BUSTING — four messages, on how many *enemy* balloons were flamed. */
function balloonBustingMessage(facts: ReviewFacts): number {
  if (clearedTheSky(facts)) return facts.friendlyLosses === 0 ? 0 : 1;
  return facts.enemyDestroyed > 0 ? 2 : 3;
}

/**
 * SCRAMBLE — five messages: DOGFIGHT's grading without the "did not locate"
 * row, and with *"inconclusive"* in place of *"without result"*.
 */
function scrambleMessage(facts: ReviewFacts): number {
  if (facts.enemyDestroyed === 0) return facts.friendlyLosses === 0 ? 0 : 2;
  if (clearedTheSky(facts)) return facts.friendlyLosses === 0 ? 1 : 4;
  return 3;
}

/**
 * DOGFIGHT A FAMOUS ACE — five messages, and the only section graded on the two
 * pilots alone rather than on counts.
 */
function aceDuelMessage(facts: ReviewFacts): number {
  if (!facts.madeContact) return 0;
  const aceDown = facts.enemyDestroyed > 0;
  if (aceDown && facts.playerLost) return 1;
  if (aceDown) return 2;
  return facts.playerLost ? 3 : 4;
}

const RULES: Readonly<Record<MissionType, (facts: ReviewFacts) => number>> = {
  dogfight: dogfightMessage,
  patrol: patrolMessage,
  escortRecon: escortReconMessage,
  escortBombers: escortBombersMessage,
  stopBombers: stopBombersMessage,
  destroyZeppelin: destroyZeppelinMessage,
  interceptZeppelin: interceptZeppelinMessage,
  balloonDefense: balloonDefenseMessage,
  balloonBusting: balloonBustingMessage,
  scramble: scrambleMessage,
};

/** Which message a family's rule picks, without expanding it. Exported for the tests. */
export function reviewMessageIndex(type: MissionType | "aceDuel", facts: ReviewFacts): number {
  return type === "aceDuel" ? aceDuelMessage(facts) : (RULES[type] as (f: ReviewFacts) => number)(facts);
}

/** `REVIEW.DAT`'s section index for a family, or the duel's. */
export function reviewSection(type: MissionType | "aceDuel"): number {
  return type === "aceDuel" ? ACE_DUEL_SECTION : (SECTION_FOR[type] as number);
}

/**
 * `%t` and `%n`, the original's own two substitutions. Applied per stored run,
 * which is the same thing as applying them to the joined paragraph — no token
 * ever straddles a run boundary in the file.
 */
function expand(line: string, facts: ReviewFacts): string {
  return line.replaceAll("%t", facts.targetNoun).replaceAll("%n", facts.aceName);
}

/**
 * The paragraph the MISSION DEBRIEFING screen draws, as its stored runs. The
 * caller joins them with a space and wraps — they are not screen rows.
 *
 * Never empty: every section has at least three messages and every rule returns
 * an index inside its own section, but the lookup is still guarded, because a
 * debriefing with no text at all would be a worse failure than a wrong one.
 */
export function reviewNarrative(type: MissionType | "aceDuel", facts: ReviewFacts): ReviewNarrative {
  const section = REVIEW_NARRATIVES[reviewSection(type)];
  if (!section) throw new Error(`REVIEW.DAT has no section for mission type "${type}"`);
  const index = reviewMessageIndex(type, facts);
  const message = section[index] ?? section[0];
  if (!message) throw new Error(`REVIEW.DAT section for "${type}" holds no messages`);
  return message.map((line) => expand(line, facts));
}
